import { TwitterCallbackUrl } from '@cubeyou-team/hx-react-components';
import React, { useCallback, useEffect, useState } from 'react';
import { ProductFruits } from 'react-product-fruits';
import { useDispatch, useSelector } from 'react-redux';

import { isGenericUs } from '@discover-frontend/utils';
import { changeDataSource } from 'src/actions/QueryActions';
import { updateDataSource } from 'src/api/me/request';
import { TRIAL_FEATURES, useTrialPaywall } from 'src/modules/trial-paywall';
import {
  addApplicationDroppingEntity,
  addApplicationTab,
  closeTwitterAuthorizationModal,
  collapseRepository,
  expandRepository,
  fullViewRepository,
  hideRepository,
  requestGenerateTwitterUrl,
  requestIgnoreTwitterToken,
  requestTimeframes,
  resetCompareError,
  showRepository,
} from '../../actions';
import { Support } from '../../components';
import { APPS_MAP } from '../../constants/config';
import {
  AudiencesOpenerContainer,
  CreateAudienceModalContainer,
  CreateTsvModalContainer,
  RepositoryContainer,
  TabsContainer,
} from '../../containers';
import { HorizontalSplit } from '../../layouts';
import { getUserInfoFromToken } from '../../utils/AuthHelperMethods';
import { AudienceActivationProvider } from '../../modules/audience-activation/providers/AudienceActivationProvider';
import { resolveDefaultDataSource } from './resolveDefaultDataSource';

const HomeContainer = () => {
  const [isDragging, setIsDragging] = useState(false);
  const dispatch = useDispatch();
  const { guardFeature, isLockedOut } = useTrialPaywall();
  const layout = useSelector((state) => state.layout);
  const tabs = useSelector((state) => state.tabs);
  const me = useSelector((state) => state.me);
  const droppingEntities = useSelector(
    (state) => state.repository.droppingEntities
  );
  const compareError = useSelector((state) => state.repository.compareError);
  const isDiscoverUS = isGenericUs();

  const handleHideRepository = useCallback(
    (event) => {
      if (event.detail) {
        const isHidden = event.detail.isHidden || false;
        isHidden ? dispatch(hideRepository()) : dispatch(showRepository());
      }
    },
    [dispatch]
  );

  const handleCloseRepository = useCallback(
    (event) => {
      window.dispatchEvent(
        new CustomEvent('TOGGLE_REPO_COLLAPSED', {
          detail: { isCollapsed: true },
        })
      );
      dispatch(collapseRepository());
    },
    [dispatch]
  );

  useEffect(() => {
    dispatch(requestTimeframes());

    window.addEventListener('CLOSE_REPOSITORY', handleCloseRepository);
    window.addEventListener('HIDE_REPOSITORY', handleHideRepository);
    return () => {
      window.removeEventListener('CLOSE_REPOSITORY', handleCloseRepository);
      window.removeEventListener('HIDE_REPOSITORY', handleHideRepository);
    };
  }, [dispatch, handleHideRepository, handleCloseRepository]);

  useEffect(() => {
    const containsCompareAudiences =
      droppingEntities &&
      droppingEntities[APPS_MAP.COMPARE] &&
      droppingEntities[APPS_MAP.COMPARE].length > 0;
    if (compareError && !containsCompareAudiences) {
      dispatch(resetCompareError());
      setIsDragging(false);
    }
    setIsDragging(containsCompareAudiences);
  }, [droppingEntities, compareError, dispatch]);

  useEffect(() => {
    if (!isDiscoverUS) {
      updateDataSource('twitter', dispatch);
      return;
    }

    const settingsDataSource = me?.data?.settings?.find(
      (item) => item.key === 'DEFAULT_DATA_SOURCE'
    );

    if (settingsDataSource?.value) {
      dispatch(
        changeDataSource(
          resolveDefaultDataSource(settingsDataSource.value.data_source)
        )
      );
    }
  }, [isDiscoverUS, me, dispatch]);

  const handleOnViewChange = useCallback(
    (size) => {
      if (size === 'collapsed' || size === 'default') {
        window.dispatchEvent(
          new CustomEvent('TOGGLE_REPO_COLLAPSED', {
            detail: { isCollapsed: size === 'collapsed' },
          })
        );
        size === 'default'
          ? dispatch(expandRepository())
          : dispatch(collapseRepository());
      } else {
        dispatch(fullViewRepository());
      }
    },
    [dispatch]
  );

  const handleOnItemDrop = useCallback(
    (e, appName) => {
      if (isLockedOut) return;

      const audience = JSON.parse(e.dataTransfer.getData('audience'));

      if ([APPS_MAP.SEGMENTATION, APPS_MAP.COMPARE].includes(appName)) {
        const isCompare = appName === APPS_MAP.COMPARE;
        return guardFeature(
          isCompare
            ? TRIAL_FEATURES.DRAG_AND_DROP_COMPARE
            : TRIAL_FEATURES.DRAG_AND_DROP_SEGMENTATION,
          () => {
            dispatch(
              isCompare
                ? addApplicationDroppingEntity(audience, appName)
                : addApplicationTab(audience, appName)
            );
          }
        );
      }

      return dispatch(addApplicationTab(audience, appName));
    },
    [dispatch, guardFeature, isLockedOut]
  );

  const handleOnItemDragToggle = useCallback(
    (isDragging) => {
      const containsCompareAudiences =
        droppingEntities &&
        droppingEntities[APPS_MAP.COMPARE] &&
        droppingEntities[APPS_MAP.COMPARE].length > 0;
      setIsDragging(isDragging || containsCompareAudiences);
    },
    [droppingEntities]
  );

  const {
    data,
    skippedRequests,
    isIgnoringTwitterToken,
    isGeneratingTwitterUrl,
    isRequestedTwitterAuthorization,
  } = me;
  const showAudiencesOpeners =
    !isLockedOut &&
    (tabs.list.length === 0 ||
      (isDragging && !tabs.selectedTabId.includes('tmp_')));

  // PRODUCT FRUITS INSTALLATION
  const workspaceId = 'enLly7RIU9r5l5hx';
  const userInfo = getUserInfoFromToken(data);
  const isCypress = typeof window !== 'undefined' && Boolean(window.Cypress);

  return (
    <React.Fragment>
      {!isCypress && (
        <ProductFruits
          workspaceCode={workspaceId}
          language="en"
          user={userInfo}
        />
      )}
      <CreateAudienceModalContainer />
      <CreateTsvModalContainer />
      <HorizontalSplit
        repositorySize={layout.repositorySize}
        repositoryHidden={layout.repositoryHidden}
        left={
          <AudienceActivationProvider>
            <RepositoryContainer
              repositorySize={layout.repositorySize}
              onViewChange={handleOnViewChange}
              onItemDragToggle={handleOnItemDragToggle}
            />
          </AudienceActivationProvider>
        }
      >
        {tabs.list.length > 0 && !isLockedOut && <TabsContainer />}
        {showAudiencesOpeners && (
          <AudiencesOpenerContainer
            onItemDrop={handleOnItemDrop}
            userName={data.name}
          />
        )}
        <Support />
      </HorizontalSplit>
      {isRequestedTwitterAuthorization && (
        <TwitterCallbackUrl
          loading={isGeneratingTwitterUrl || isIgnoringTwitterToken}
          snoozeLeftCount={3 - skippedRequests}
          connect={() => dispatch(requestGenerateTwitterUrl())}
          snooze={() => {
            dispatch(requestIgnoreTwitterToken());
          }}
          close={() => dispatch(closeTwitterAuthorizationModal())}
        />
      )}
    </React.Fragment>
  );
};

export default HomeContainer;
